import { mapDeviceAuthenticationFromSysApi } from '@shure/shared/angular/data-access/device-authentication';
import {
	mapCoverageModeFromSysApi,
	mapDeviceCompatibilityLevelFromSysApi,
	mapDeviceConferencingOperationModeFromSysApi,
	mapDeviceLicenseFromSysApi,
	mapDeviceLicenseWarningDetailsFromSysApi,
	mapDeviceModelFromSysApi,
	mapFirmwareFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { EthernetPowerSourceType } from '@shure/shared/angular/data-access/system-api/models';

import { IntellimixChannel, IntellimixDevice } from '../../api/models';
import { IntellimixDeviceFragment } from '../graphql/generated/intellimix.generated';

import { mapAutomixChannelFromSysApi } from './channel.mapper';

export function mapIntellimixDeviceFromSysApi(
	deviceSysApi: IntellimixDeviceFragment,
	authenticated: boolean | false,
	readonlyModeSelected: boolean | false
): IntellimixDevice {
	const {
		id,
		status,
		compatibility,
		description: { features, model, constraints },
		features: {
			coverageMode,
			audioChannels,
			virtual,
			automixGainMetering,
			allIntellimixBypass,
			allEqualizersBypass,
			authentication,
			danteAudioNetworkAccess,
			audioChannelCount,
			virtualAcousticBoundary,
			license,
			loudspeaker,
			ethernetPowerSource,
			conferencingOperationMode,
			dectRfSpectrumScan
		}
	} = deviceSysApi;

	let mappedChannels: IntellimixChannel[] = [];
	if (audioChannels?.audioChannels) {
		mappedChannels = audioChannels.audioChannels.map((channel) => mapAutomixChannelFromSysApi(channel));
	}

	const lobeChannelSupported = audioChannels?.audioChannels.some(
		(audioChannel) => audioChannel.description.features.lobeConfiguration.supported
	);
	const coverageAreaSupported = features.audioCoverageAreas.supported;

	return {
		id,
		compatibility: mapDeviceCompatibilityLevelFromSysApi(compatibility),
		status,
		model: mapDeviceModelFromSysApi(model),
		isDevicePreviewSupported: lobeChannelSupported || coverageAreaSupported,
		features: {
			virtual: {
				virtual: virtual?.virtual ?? true,
				isMissing: !features.virtual.supported
			},
			coverageMode: {
				coverageMode: mapCoverageModeFromSysApi(coverageMode?.coverageMode),
				isMissing: !features.coverageMode.supported
			},
			virtualAcousticBoundary: features.virtualAcousticBoundary.supported
				? {
						enabled: virtualAcousticBoundary?.enabled ?? false,
						sensitivity: virtualAcousticBoundary?.sensitivity ?? 0,
						isMissing: !virtualAcousticBoundary
					}
				: undefined,
			channels: {
				channels: mappedChannels,
				isMissing: !features.audioChannels.supported
			},
			automixGainMetering: {
				enabled: automixGainMetering?.enabled || false,
				isMissing: !features.automixGainMetering.supported
			},
			allIntellimixBypass: {
				bypassed: allIntellimixBypass?.bypassed ?? false,
				isMissing: !features.allIntellimixBypass.supported
			},
			allEqualizersBypass: {
				bypassed: allEqualizersBypass?.bypassed ?? false,
				isMissing: !features.allEqualizersBypass.supported
			},
			authentication: mapDeviceAuthenticationFromSysApi(authentication, authenticated, readonlyModeSelected),
			danteNetworkAccess: {
				changesAllowed: danteAudioNetworkAccess?.changesAllowed ?? false,
				factoryResetAllowed: danteAudioNetworkAccess?.factoryResetAllowed ?? false,
				isMissing: !features.danteAudioNetworkAccess.supported
			},
			firmware: mapFirmwareFromSysApi(deviceSysApi),
			license: features.license.supported
				? {
						...mapDeviceLicenseFromSysApi(license?.info, (v) =>
							mapDeviceLicenseWarningDetailsFromSysApi(v, audioChannelCount?.count ?? 0)
						),
						isMissing: license === null
					}
				: undefined,
			loudspeaker: features.loudspeaker.supported
				? {
						available: loudspeaker?.available ?? false,
						enabled: loudspeaker?.enabled ?? false,
						isMissing: !loudspeaker
					}
				: undefined,
			ethernetPowerSource: {
				type: ethernetPowerSource?.type ?? EthernetPowerSourceType.Unknown,
				isMissing: !features.ethernetPowerSource.supported
			},
			conferencingOperationMode: features.conferencingOperationMode.supported
				? {
						mode: mapDeviceConferencingOperationModeFromSysApi(conferencingOperationMode?.mode),
						isMissing: !conferencingOperationMode
					}
				: undefined,
			dectRfSpectrumScan: features.dectRfSpectrumScan.supported
				? {
						inProgress: dectRfSpectrumScan?.inProgress ?? false,
						isMissing: !dectRfSpectrumScan
					}
				: undefined
		},
		constraints: {
			virtualAcousticBoundary: {
				sensitivity: {
					range: {
						min: constraints.virtualAcousticBoundary?.sensitivity?.range.min ?? 1,
						max: constraints.virtualAcousticBoundary?.sensitivity?.range.max ?? 20,
						step: constraints.virtualAcousticBoundary?.sensitivity?.range.step ?? 1
					}
				}
			},
			conferencingOperationMode: constraints.conferencingOperationMode
				? {
						options: constraints.conferencingOperationMode.mode.options.map((option) =>
							mapDeviceConferencingOperationModeFromSysApi(option)
						)
					}
				: undefined
		}
	};
}
